import { exists } from '@jack-henry/frontend-utils/functions';

/**
 * Maximum safe integer value for JavaScript number precision.
 * Any larger values will cause rounding.
 */
const MAX_SAFE_CURRENCY_VALUE = 9999999999999.99;

/**
 * Maximum raw input length (digits + decimal point).
 * Based on 9999999999999.99 = 16 characters
 */
export const MAX_RAW_INPUT_LENGTH = 16;

/**
 * Maximum formatted currency display length (includes $, commas, decimals).
 * Used for HTML input maxLength attribute.
 * Based on: $9,999,999,999,999.99 = 21 characters
 */
export const MAX_CURRENCY_DISPLAY_LENGTH = 21;

export interface CurrencyFormatOptions {
    symbol?: string;
    decimals?: number;
    locale?: string;
}

/**
 * Formats a number as a complete currency value with proper decimal places
 * @param value - The numeric value
 * @param options - Currency formatting options
 * @returns The formatted currency string
 */
export function formatCurrencyComplete(value: number, options: CurrencyFormatOptions = {}): string {
    const { symbol = '$', decimals = 2, locale = 'en-US' } = options;

    const formatted = value.toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });

    return symbol + formatted;
}

/**
 * Formats USD currency using native Intl API
 * @param amount - The numeric amount
 * @returns The formatted USD currency string
 */
export function formatUsdCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(amount);
}

/**
 * Formats currency to ensure proper decimal places (e.g., for blur events)
 * @param value - The current currency string value
 * @param options - Currency formatting options
 * @returns The formatted currency string with proper decimal places
 */
export function formatCurrencyDecimals(value: string, options: CurrencyFormatOptions = {}): string {
    const { decimals = 2 } = options;

    if (!value) {
        return value;
    }

    // Ensure exactly the correct number of decimal places
    const [integerPart, decimalPart = ''] = value.split('.');
    const paddedDecimal = decimalPart.padEnd(decimals, '0');
    return `${integerPart}.${paddedDecimal}`;
}

/**
 * Applies currency masking with intelligent formatting during typing vs complete values
 * @param value - The raw input value
 * @param options - Currency formatting options
 * @returns The masked currency value
 */
export function applyCurrencyMask(value: string, options: CurrencyFormatOptions = {}): string {
    const { symbol = '$', decimals = 2, locale = 'en-US' } = options;

    // Handle empty input
    if (!value) {
        return '';
    }

    // Clean input: remove everything except digits and decimal points
    let cleanValue = value.replace(/[^\d.]/g, '');

    // Handle completely empty after cleaning
    if (!cleanValue) {
        return '';
    }

    // Handle just a decimal point
    if (cleanValue === '.') {
        return symbol + '0.';
    }

    // Handle decimal at start (.99 -> 0.99)
    if (cleanValue.startsWith('.')) {
        cleanValue = '0' + cleanValue;
    }

    // Handle length limit intelligently. Prioritize keeping the decimal structure intact
    if (cleanValue.length > MAX_RAW_INPUT_LENGTH) {
        const parts = cleanValue.split('.');
        if (parts.length === 2) {
            // Has decimal part - we need to decide what to truncate
            const [integerPart, decimalPart] = parts;

            // If decimal part exceeds the allowed decimals, truncate it first
            if (decimalPart.length > decimals) {
                const truncatedDecimalPart = decimalPart.substring(0, decimals);
                cleanValue = integerPart + '.' + truncatedDecimalPart;
            }

            // If still too long after decimal truncation, truncate from the end
            if (cleanValue.length > MAX_RAW_INPUT_LENGTH) {
                cleanValue = cleanValue.substring(0, MAX_RAW_INPUT_LENGTH);
            }
        } else {
            // No decimal part, simple truncation
            cleanValue = cleanValue.substring(0, MAX_RAW_INPUT_LENGTH);
        }
    }

    // Handle multiple decimal points - keep only the first one
    const firstDecimalIndex = cleanValue.indexOf('.');
    if (firstDecimalIndex !== -1) {
        const beforeDecimal = cleanValue.substring(0, firstDecimalIndex);
        const afterDecimal = cleanValue.substring(firstDecimalIndex + 1).replace(/\./g, '');
        cleanValue = beforeDecimal + '.' + afterDecimal;
    }

    // Limit decimal places (this should now be redundant but kept for safety)
    const parts = cleanValue.split('.');
    if (parts.length === 2 && parts[1].length > decimals) {
        cleanValue = parts[0] + '.' + parts[1].substring(0, decimals);
    }

    // Validate the numeric value
    const numValue = parseFloat(cleanValue);
    if (isNaN(numValue) || numValue < 0) {
        return symbol;
    }

    // Check against MAX_SAFE_CURRENCY_VALUE and truncate intelligently
    if (numValue > MAX_SAFE_CURRENCY_VALUE) {
        const parts = cleanValue.split('.');
        const maxSafeIntegerLength = 13; // Length of '9999999999999'

        // Truncate the user's integer part to safe length, preserving their input pattern
        if (parts[0].length > maxSafeIntegerLength) {
            const truncatedIntegerPart = parts[0].substring(0, maxSafeIntegerLength);
            cleanValue = truncatedIntegerPart + (parts[1] ? '.' + parts[1] : '');
        } else {
            // If integer length is OK but value still exceeds, truncate to safe length
            const truncatedIntegerPart = parts[0].substring(0, maxSafeIntegerLength);
            cleanValue = truncatedIntegerPart + (parts[1] ? '.' + parts[1] : '');
        }
    }

    // Format the output
    const finalParts = cleanValue.split('.');
    const [integerPart = '0', decimalPart] = finalParts;

    // Format integer part with commas
    const formattedInteger = parseInt(integerPart).toLocaleString(locale);

    // Build the final result
    if (decimalPart !== undefined) {
        // Has decimal part (could be empty if user just typed '.')
        return `${symbol}${formattedInteger}.${decimalPart}`;
    } else {
        // No decimal point
        return `${symbol}${formattedInteger}`;
    }
}

/**
 * Parses a currency string to extract the raw numeric value
 * @param value - The currency string (e.g., "$1,234.56")
 * @returns The numeric value as a string (e.g., "1234.56")
 */
export function parseCurrencyValue(value: string): string {
    return value.replace(/[^\d.-]/g, '');
}

/**
 * Validates if a currency string is properly formatted
 * @param value - The currency string to validate
 * @param options - Currency formatting options
 * @returns True if valid, false otherwise
 */
export function isValidCurrency(value: string, options: CurrencyFormatOptions = {}): boolean {
    const { symbol = '$', decimals = 2 } = options;

    if (!value || !value.startsWith(symbol)) {
        return false;
    }

    // Check length limit using constant
    if (value.length > MAX_CURRENCY_DISPLAY_LENGTH) {
        return false;
    }

    const numericPart = parseCurrencyValue(value);
    const numValue = parseFloat(numericPart);

    // Reject negative numbers
    if (numValue < 0) {
        return false;
    }

    const parts = numericPart.split('.');

    // Check decimal places
    if (parts.length === 2 && parts[1].length !== decimals) {
        return false;
    }

    // Check if it's a valid number
    return !isNaN(numValue);
}

/**
 * Given a number or a string, format it for display as a currency value with exactly two decimal places.
 * This simulates the behavior of an ATM input, where the last two digits are always the decimal portion.
 */
export function formatCurrencyAtm(value?: string | number) {
    if (!exists(value)) {
        return '0.00';
    }

    if (typeof value === 'number') {
        value = value.toString();
    }

    if (value === '') {
        return '0.00';
    }

    if (isNaN(Number.parseFloat(value))) {
        throw new Error(`Value "${value}" is not a valid number`);
    }

    const stripped = normalizeValue(value);
    const integerPortion = stripped.slice(0, stripped.length - 2).padStart(1, '0');
    const decimalPortion = stripped.slice(-2).padStart(2, '0');

    // this must happen as string manipulation to preserve trailing zeros;
    // otherwise, dividing by 100 or calling toLocaleString() will drop them
    return `${integerPortion}.${decimalPortion}`;

    /**
     * Given a formatted numeric string, produce a version with all non-numeric characters removed and leading zeroes trimmed.
     */
    function normalizeValue(value: string) {
        return value.replace(/\D/g, '').replace(/^0+/, '');
    }
}
